import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';

import { RouteNames } from 'config/routeNames';
import { FilterOptionModel, SmOrderTaskSummaryModel } from 'models';
import {
  LeoSelectFieldMulti,
  Page,
  Table,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import {
  useExportNotCompletedAfterDeadLine,
  useNotCompletedAfterDeadLineList,
} from 'features/notCompletedAfterDeadline';
import { useValueSetList } from 'features/valueSets';
import { useWorkerListSearch } from 'features/workers';
import { useFieldDataAndAccessor } from 'utils/useFieldDataAndAccessor';
import { getValueSetValues } from 'utils/valueSetFunctions';
import { getPageName } from 'utils/format';
import Yup from 'utils/yup';
import LeoDateInterval, {
  relativeDateTestFunction,
} from 'components/input-fields/leoDateInterval/LeoDateInterval';

import { ReactComponent as Export } from 'assets/icons/Material/fileDownload.svg';
import { formatDate } from 'utils/dates';

const RegisterValidator = () => {
  return t('ERRORS.INVALID_DATE');
};

const validationSchema = Yup.object().shape({
  appointmentDate: Yup.object()
    .nullable()
    .shape({
      from: Yup.mixed()
        .nullable()
        .required()
        .test('isDate validation', RegisterValidator, relativeDateTestFunction),
      to: Yup.mixed()
        .nullable()
        .test('isDate validation', RegisterValidator, relativeDateTestFunction),
    }),
  badInterval: Yup.bool().oneOf([true], 'bad interval'),
});

const NotCompletedAfterDeadlinePage: FC = () => {
  const {
    onPageChange,
    onRowsPerPageChange,
    onLoadQuery,
    onFilter,
    onSort,
    params,
    apiParams,
    exportParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams();

  const {
    data: notCompletedAfterDeadLineList,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useNotCompletedAfterDeadLineList(apiParams);
  const { data: workersData } = useWorkerListSearch({ withDeleted: true });
  const { data: valueSet, isLoading: isValueSetLoading } = useValueSetList({
    code: ['TASK_STATUS', 'APPOINTMENT_TIME'],
  });

  const { accessor: smOrderStatusAccessor } = useFieldDataAndAccessor(
    getValueSetValues(valueSet, 'TASK_STATUS')
  );
  const { accessor: appointmentTimeAccessor } = useFieldDataAndAccessor(
    getValueSetValues(valueSet, 'APPOINTMENT_TIME')
  );
  const { data: workerData, accessor: workerAccessor } = useFieldDataAndAccessor(
    workersData,
    'sapId',
    'name',
    true
  );

  const { isInitialLoading, isRefetching, refetch } =
    useExportNotCompletedAfterDeadLine(exportParams);

  const headerActions = [
    {
      name: 'export',
      label: 'COMMON.EXPORT',
      icon: <Export fill="currentColor" />,
      onClick: () => refetch(),
      isLoading: isInitialLoading || isRefetching,
      disabledIf: !exportParams?.filter,
    },
  ];

  const filters: FilterOptionModel[] = [
    {
      name: 'appointmentDate',
      headerTitle: 'UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.APPOINTMENT_DATE',
      component: <LeoDateInterval name="appointmentDate" />,
      panelFieldWidth: 12,
      panelFieldOffset: 12,
      divider: true,
      abbrName: t('FILTERABBR.APPOINTMENT_DATE'),
      filterIsRequired: true,
    },
    {
      name: 'worker',
      headerTitle: 'UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.WORKER_CODE',
      component: (
        <LeoSelectFieldMulti
          name="worker"
          data={workerData}
          accessor={workerAccessor}
          label="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.WORKER_CODE"
        />
      ),
      divider: true,
      panelFieldWidth: 12,
    },
  ];

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK),
    validationSchema,
    isFetching,
  });

  const panels = [filterConfig];

  const { activePanel, closePanel, setPanel } = usePanelState();

  const deadlineColumns = (smOrderStatusAccess: any, appointmentTimeAccess: any) => [
    {
      key: 'orderId',
      header: <Trans i18nKey="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.ORDER_ID" />,
      accessor: 'orderId',
      sortable: true,
    },
    {
      key: 'taskStatus',
      header: <Trans i18nKey="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.TASK_STATUS" />,
      accessor: (item: SmOrderTaskSummaryModel) => smOrderStatusAccess[item.taskStatus],
      field: 'taskStatus',
      sortable: true,
    },
    {
      key: 'worker.sapId',
      header: <Trans i18nKey="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.WORKER_CODE" />,
      accessor: (item: SmOrderTaskSummaryModel) => item.worker?.sapId,
      field: 'worker.sapId',
      sortable: true,
    },
    {
      key: 'appointmentDate',
      header: <Trans i18nKey="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.APPOINTMENT_DATE" />,
      accessor: (item: SmOrderTaskSummaryModel) =>
        `${formatDate(item.appointmentDate)} ${appointmentTimeAccess[item.appointmentTime] || ''}`,
      field: 'appointmentDate',
      sortable: true,
    },
  ];

  return (
    <Page
      panels={panels}
      activePanel={activePanel}
      title={<Trans i18nKey="UCSE_HATARIDORE_NEM_TELJESULT_FELADATOK.TITLE" />}
      inScroll
      closePanel={closePanel}
      setPanel={setPanel}
    >
      {!isValueSetLoading && (
        <Table
          panels={panels}
          activePanel={activePanel}
          setPanel={setPanel}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          list={notCompletedAfterDeadLineList?.data || []}
          total={notCompletedAfterDeadLineList?.meta.total}
          timestamp={dataUpdatedAt}
          loading={isLoading}
          enableCheckbox={false}
          enableTableView={true}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={deadlineColumns(smOrderStatusAccessor, appointmentTimeAccessor)}
          pageHeaderActions={headerActions}
          onLoadQuery={onLoadQuery}
        />
      )}
    </Page>
  );
};
export default NotCompletedAfterDeadlinePage;
